<script lang="ts" setup>
interface CardProps {
  withPadding?: boolean
  variant?: "default" | "primary" | "secondary" | "offwhite" | "pink-border" | "shadow" | "danger" | "subtle"
  borderRadius?: "default" | "sm" | "lg" | "xl" | "2xl" | "3xl"
}

withDefaults(defineProps<CardProps>(), {
  withPadding: true,
  variant: "default",
  borderRadius: "default",
})
</script>

<template>
  <div class="gpx-card bg-white rounded-xl" :class="[`gpx-card_${variant}`, `gpx-card_radius-${borderRadius}`]">
    <div v-if="$slots.header" class="gpx-card-header" :class="`gpx-card-header_radius-${borderRadius}`">
      <slot name="header" />
    </div>
    <div
      class="gpx-card-body" :class="{
        'gpx-card-body_with-header': $slots.header,
        'gpx-card-body_with-footer': $slots.footer,
        'gpx-card-body-pad': withPadding,
      }"
    >
      <slot />
    </div>
    <div v-if="$slots.footer" :class="`gpx-card-footer_radius-${borderRadius}`">
      <slot name="footer" />
    </div>
  </div>
</template>

<style lang="postcss" scoped>
@reference "../../../assets/styles.css"

.gpx-card {
  --edge-padding: 1.25rem;

  @apply relative flex flex-col;
}

.gpx-card_primary {
  @apply bg-primary text-primary-card-text;
}

.gpx-card_secondary {
  @apply bg-secondary text-secondary-card-text border;

  border: var(--template-border);
}

.gpx-card_offwhite {
  @apply bg-gray-200 text-gray-700;
}

.gpx-card_pink-border {
  background-color: #fcf3f3;

  @apply text-primary border border-nav-active;
}

.gpx-card_shadow {
  @apply border border-white/40 shadow-xl;
}

.gpx-card_danger {
  @apply border border-danger bg-theme-danger/10;
}

.gpx-card_subtle {
  @apply bg-white border border-gray-300;
}

.gpx-card_radius-sm {
  border-radius: 8px;
}

.gpx-card_radius-lg {
  border-radius: 16px;
}

.gpx-card_radius-xl {
  border-radius: 24px;
}

.gpx-card_radius-2xl {
  border-radius: 32px;
}

.gpx-card_radius-3xl {
  border-radius: 40px;
}

.gpx-card-header {
  padding: var(--edge-padding) var(--edge-padding) 0;
  margin-bottom: 0;
  border-radius: 12px 12px 0 0;
}

.gpx-card-header_radius-sm {
  border-radius: 8px 8px 0 0;
}

.gpx-card-header_radius-lg {
  border-radius: 16px 16px 0 0;
}

.gpx-card-header_radius-xl {
  border-radius: 24px 24px 0 0;
}

.gpx-card-header_radius-2xl {
  border-radius: 32px 32px 0 0;
}

.gpx-card-header_radius-3xl {
  border-radius: 40px 40px 0 0;
}

.gpx-card-footer_radius-sm {
  border-radius: 0 0 8px 8px;
}

.gpx-card-footer_radius-lg {
  border-radius: 0 0 16px 16px;
}

.gpx-card-footer_radius-xl {
  border-radius: 0 0 24px 24px;
}

.gpx-card-footer_radius-2xl {
  border-radius: 0 0 32px 32px;
}

.gpx-card-footer_radius-3xl {
  border-radius: 0 0 40px 40px;
}

.gpx-card-body {
  flex: 1 1 auto;
}

.gpx-card-body-pad {
  padding: var(--edge-padding);
}

.gpx-card-body_with-header {
  padding-top: 0;
}

.gpx-card-body_with-footer {
  padding-bottom: 0;
}

@media (width >=768px) {
  .gpx-card {
    --edge-padding: 2rem;
  }
}
</style>
